/**
 * Lead-score heat indicator for the contacts table.
 *
 * A contact's lead_score is a 0–100 signal. We surface it as a small pill with
 * a heat color so a rep can eyeball the hottest records at a glance:
 *   low  (0–39)   → zinc    (cold)
 *   mid  (40–69)  → amber    (warming)
 *   high (70–100) → emerald  (hot)
 */

export function leadScoreTier(score: number): "low" | "mid" | "high" {
  if (score >= 70) return "high";
  if (score >= 40) return "mid";
  return "low";
}

const TIER_CLASS: Record<"low" | "mid" | "high", string> = {
  low: "border-zinc-700/60 bg-zinc-800/50 text-zinc-400",
  mid: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  high: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
};

export function LeadScoreBadge({ score }: { score: number | null | undefined }) {
  const n = typeof score === "number" && Number.isFinite(score) ? Math.max(0, Math.min(100, Math.round(score))) : null;
  if (n === null) {
    return <span className="text-zinc-600">—</span>;
  }
  const tier = leadScoreTier(n);
  return (
    <span
      className={
        "inline-flex min-w-[2.25rem] items-center justify-center rounded-md border px-1.5 py-0.5 text-xs font-medium tabular-nums " +
        TIER_CLASS[tier]
      }
      title={`Lead score: ${n}/100`}
    >
      {n}
    </span>
  );
}
